import Axios from "axios";

export interface TtsGenerationRequest {
  languageId: string;
  voiceName: string;
  voiceVersion: number;
  text: string;
  ssml?: string;
  isRawSsml?: boolean;
  breakTime?: number;
  prosody?: number;
  featureName: string;
}

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/age.py
export const TTS_GENERATION_AGES = [
  "child",
  "teen",
  "adult",
  "mature",
  "elderly",
] as const;
export type TtsGenerationAge = (typeof TTS_GENERATION_AGES)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/character.py
export const TTS_GENERATION_CHARACTERS = [
  "bea",
  "eddy",
  "junior",
  "falstaff",
  "lily",
  "lin",
  "lucy",
  "oscar",
  "vikram",
  "zari",
] as const;
export type TtsGenerationCharacter = (typeof TTS_GENERATION_CHARACTERS)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/gender.py
export const TTS_GENERATION_GENDERS = ["male", "female", "other"] as const;
export type TtsGenerationGender = (typeof TTS_GENERATION_GENDERS)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/provider.py
export const TTS_GENERATION_PROVIDERS = [
  "azure",
  "elevenlabs",
  "google",
  "amazon",
  "openai",
] as const;
export type TtsGenerationProvider = (typeof TTS_GENERATION_PROVIDERS)[number];

export type TtsGenerationSpeed = "slow" | "normal" | "fast";

// https:// github.com/duolingo/tts-generation-backend/blob/master/application/models/voice_status.py
export const TTS_GENERATION_STATUSES = [
  "draft",
  "active",
  "deprecated",
  "unavailable",
] as const;
export type TtsGenerationStatus = (typeof TTS_GENERATION_STATUSES)[number];

export type TtsGenerationCustomVoiceEndpointStatus =
  | "SUSPENDED"
  | "SPINNING_DOWN"
  | "SPINNING_UP"
  | "ACTIVE"
  | "UNKNOWN";

const ttsGenerationClient = Axios.create({
  baseURL: `${process.env.REACT_APP_TTS_GENERATION_ORIGIN}`,
  withCredentials: true,
});

export interface LanguageItem {
  /** BCP 47 language tag (e.g., en-US) */
  bcp47: string;
  default_dialect: boolean;
  /** Two-letter abbreviation for language pairs in course directions (e.g., zc in zc<-zs = Cantonese course for native Mandarin speakers) */
  duo_code: string | null;
  /** Duolingo internal language ID (e.g. zh-HK for Cantonese) */
  duo_id: string | null;
  example_sentence: string;
  /** Whether the course uses recordings of live human speech (vs. synthetic TTS audio) */
  is_recorded_lang: boolean;
  /** Whether that language is actively taught in a course */
  is_target_lang: boolean;
  /** True if some courses exist that are instructed in that language */
  is_ui_lang: boolean;
  /** ISO 639-1 two-letter code (e.g., en for English) */
  iso1: string;
  /** ISO 639-3 three-letter code (e.g., eng for English) */
  iso3: string;
  language_group: string | null;
  name: string;
  scripts: string[];
  whitespace_delimited: boolean;
}

export type CreateVoicePayload = Omit<
  VoicesV2Item,
  "added_at" | "id" | "quality_badge"
> & {
  is_default_version: true;
  version: 1;
};

export type UpdateVoicePayload = Partial<
  Omit<VoicesV2Item, "added_at" | "added_by" | "id" | "quality_badge">
> & {
  last_updated_by: string;
};

// v2 voices API response item
export interface VoicesV2Item {
  added_at: string | null;
  added_by: string;
  age: TtsGenerationAge | null;
  friendly_name: string | null;
  id: string;
  is_always_available?: boolean;
  is_default_version: boolean;
  is_custom_voice?: boolean;
  is_expressive?: boolean;
  is_multilingual?: boolean;
  language_statuses: { language: string; qa_status: string }[];
  last_updated_at: string | null;
  last_updated_by: string;
  name: string;
  notes: string;
  gender: TtsGenerationGender | null;
  primary_language: string;
  provider: TtsGenerationProvider;
  provider_parameters: {
    deployment_id?: string | null;
    model_name: string;
    pitch?: number | null;
    rate?: number | null;
    similarity_boost?: number | null;
    stability?: number | null;
    style?: number | null;
    use_speaker_boost?: boolean | null;
    volume?: number | null;
  };
  speed: TtsGenerationSpeed | null;
  status: TtsGenerationStatus;
  tags: string[];
  use_cases: string[];
  version: number;
  world_character: TtsGenerationCharacter | null;
  quality_badge?: "high" | "acceptable" | "low" | null;
}

const fetchAllVoices = async (): Promise<VoicesV2Item[]> =>
  (
    await ttsGenerationClient.get<VoicesV2Item[]>("/v2/voices", {
      params: { all_versions: "true", include_quality: "true" },
    })
  ).data;

let voicesMemo: VoicesV2Item[] | undefined;
const ensureVoicesMemo = async () => {
  if (!voicesMemo) {
    voicesMemo = await fetchAllVoices();
  }
  return voicesMemo;
};

export const getTtsGenerationVoiceList = async () => ensureVoicesMemo();

export const invalidateVoiceListCache = () => {
  voicesMemo = undefined;
};

export const getTtsGenerationVoice = async (
  voiceName: string,
): Promise<VoicesV2Item | undefined> => {
  const voices = await ensureVoicesMemo();
  return (
    voices.find(v => v.name === voiceName && v.is_default_version) ??
    voices.find(v => v.name === voiceName)
  );
};

export const getLanguages = async () =>
  (await ttsGenerationClient.get<LanguageItem[]>("/v1/languages")).data;

export const hasSuspendableEndpoint = (voice: VoicesV2Item) =>
  voice.provider === "azure" &&
  !!voice.is_custom_voice &&
  !!voice.provider_parameters.deployment_id &&
  !voice.is_always_available &&
  voice.status === "active";

export const getCustomVoiceEndpointStatus = async (voiceName: string) =>
  (await getCustomVoiceEndpointStatuses([voiceName]))[voiceName];

export const getCustomVoiceEndpointStatuses = async (voiceNames: string[]) =>
  (
    await ttsGenerationClient.get<
      Record<string, TtsGenerationCustomVoiceEndpointStatus>
    >("/endpoint/statuses", {
      params: {
        voiceNames: voiceNames.join(","),
      },
    })
  ).data;

export const spinUpCustomVoiceEndpoint = (voiceName: string) =>
  spinUpCustomVoiceEndpoints([voiceName]);

export const spinUpCustomVoiceEndpoints = (voiceNames: string[]) =>
  ttsGenerationClient.post<"Accepted">(
    "/endpoint/spin-up",
    {
      voiceNames,
    },
    { validateStatus: status => status === 202 },
  );

export const getTtsGenerationAudio = async (
  requestData: TtsGenerationRequest,
) =>
  (
    await ttsGenerationClient.post<Blob>(
      "/tts/sync/file",
      {
        consumer_name: "tts-data-web",
        feature_name: requestData.featureName,
        language_id: requestData.languageId,
        team_name: "tts-and-localization",
        texts: [
          {
            break_time: requestData.breakTime,
            content: requestData.text,
            prosody_rate: requestData.prosody,
            raw_ssml: requestData.isRawSsml ? requestData.ssml : undefined,
            ssml_body: requestData.isRawSsml ? undefined : requestData.ssml,
            voice_version: requestData.voiceVersion,
          },
        ],
        voice_names: [requestData.voiceName],
      },
      {
        headers: {
          Accept: "application/json",
        },
        responseType: "blob",
      },
    )
  ).data;

export const createVoice = async (payload: CreateVoicePayload) =>
  (await ttsGenerationClient.post<VoicesV2Item>("/voices", payload)).data;

export const patchVoice = async (id: string, payload: UpdateVoicePayload) =>
  (await ttsGenerationClient.patch<VoicesV2Item>(`/voices/${id}`, payload))
    .data;

export interface VoiceEvalScore {
  avg: number;
  count: number;
  scaleMin: number;
  scaleMax: number;
  scope: string;
  dimension: string;
}

export interface VoiceEvalEntry {
  evalName: string;
  evalType: string;
  languageId: string | null;
  createdAt: string;
  scores: Record<string, VoiceEvalScore>;
}

export interface VoiceEvalResults {
  voiceName: string;
  voiceProvider: string;
  voiceVersion: number;
  aggregateScores: Record<string, VoiceEvalScore>;
  qualityBadge: "high" | "acceptable" | "low" | null;
  evals: VoiceEvalEntry[];
}

interface VoiceDetailResponse extends VoicesV2Item {
  quality?: VoiceEvalResults;
}

export const getVoiceDetail = async (
  voiceName: string,
  version: number,
): Promise<VoiceDetailResponse> =>
  (
    await ttsGenerationClient.get<VoiceDetailResponse>(
      `/v2/voices/${encodeURIComponent(voiceName)}`,
      {
        params: { include_quality: "true", version },
      },
    )
  ).data;
